import React, { useEffect, useRef, useState } from "react";
import DateRangePicker from "components/common/DateRangePicker";
import { Icons } from "assets/icons";
import { useTranslation } from "react-i18next";
import { MONTHS } from "utils/constants";

const FILTERS = ["DAY", "WEEK", "MONTH"];
const FILTER_LABELS = {
  DAY: "Day",
  WEEK: "Week",
  MONTH: "Month",
};

const getWeekStart = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
};

const getWeekEnd = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() + (6 - d.getDay()));
  d.setHours(23, 59, 59, 999);
  return d;
};

/**
 * Given a raw clicked date, snap to the correct from/to
 * based on the active filter type.
 *
 * DAY   → same day (00:00 → 23:59)
 * WEEK  → Sunday of that week → Saturday of that week
 * MONTH → 1st of that month  → last of that month
 *
 * Result is always clamped so `to` never exceeds today.
 */
const snapDates = (type, rawFrom) => {
  const base = rawFrom || new Date();

  switch (type) {
    case "DAY": {
      const from = new Date(base);
      from.setHours(0, 0, 0, 0);
      const to = new Date(base);
      to.setHours(23, 59, 59, 999);
      return { from, to }; // ← no clamp
    }

    case "WEEK": {
      const from = getWeekStart(base);
      const to = getWeekEnd(base);
      return { from, to }; // ← no clamp
    }

    case "MONTH": {
      const from = new Date(base.getFullYear(), base.getMonth(), 1, 0, 0, 0, 0);
      const to = new Date(
        base.getFullYear(),
        base.getMonth() + 1,
        0,
        23,
        59,
        59,
        999,
      );
      return { from, to }; // ← no clamp
    }

    default: {
      const now = new Date();
      return { from: now, to: now };
    }
  }
};

/**
 * Returns the full period range for ← → navigation.
 *
 * DAY / WEEK → offset moves by calendar month
 * MONTH      → offset moves by calendar year
 */
const getDateRange = (type, offset = 0) => {
  const now = new Date();

  switch (type) {
    case "DAY": {
      const from = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + offset,
        0,
        0,
        0,
        0,
      );
      const to = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + offset,
        23,
        59,
        59,
        999,
      );
      return { from, to };
    }

    case "WEEK": {
      // For offset 0, snap from today — so we get current week's Sun→Sat
      const anchor = new Date(now);
      anchor.setDate(now.getDate() + offset * 7);

      const from = getWeekStart(anchor);
      const to = getWeekEnd(anchor);

      return { from, to };
    }

    case "MONTH": {
      const month = now.getMonth() + offset; // handles year rollover automatically
      const from = new Date(now.getFullYear(), month, 1, 0, 0, 0, 0);
      const to = new Date(now.getFullYear(), month + 1, 0, 23, 59, 59, 999);
      return { from, to };
    }

    default:
      return { from: new Date(), to: new Date() };
  }
};

const isCurrentPeriod = (offset) => offset >= 0;

const CustomerLeadsFilter = ({
  dates,
  setDates,
  filters = [],
  selectedFilters = {},
  setSelectedFilters = () => {},
  activeTabKey,
  setActiveFilter,
  className = "dashboard_filter",
  selectTypeLabel = true,
  selectDateLabel = true,
}) => {
  const [active, setActive] = useState("");
  const [offset, setOffset] = useState(0);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { refreshDashboard } = Icons || {};
  const pickerRef = useRef(null);

  const applyWithOffset = (type, newOffset) => {
    const { from, to } = getDateRange(type, newOffset);
    setOffset(newOffset);
    setDates([from, to]);
    setActive(type);
    setActiveFilter(type);
  };

  const applyPreset = (type) => applyWithOffset(type, 0);

  const handlePrev = () => {
    if (!active) return;
    applyWithOffset(active, offset - 1);
  };

  const handleNext = () => {
    if (!active || isCurrentPeriod(offset)) return;
    applyWithOffset(active, offset + 1);
  };

  const handleReset = () => {
    setDates([]);
    applyWithOffset("DAY", 0);
    setOffset(0);
    setSelectedFilters({});
    setActiveFilter("");
  };

  /**
   * User clicked a date in the calendar.
   *
   * The picker fires with [from] mid-selection or [from, to] on completion.
   * We only snap once BOTH ends arrive — that way the picker's internal
   * "first click / second click" flow isn't interrupted.
   *
   * For DAY the picker fires [date, date] so the snap runs immediately.
   */
  const handleManualDateChange = (newDates) => {
    if (!active || !newDates?.length) return;

    const rawFrom = newDates[0] ?? null;
    if (!rawFrom) return;

    const { from, to } = snapDates(active, rawFrom);
    setDates([from, to]);
    setOffset(null);

    // Close the picker after snapping
    pickerRef.current?.hide();
  };

  const rightDisabled = !active || isCurrentPeriod(offset);
  const leftDisabled = !active;

  useEffect(() => {
    applyWithOffset("DAY", 0);
  }, []);

  return (
    <div className={`${className} g_end gap-2 common_filter`}>
      {/* Preset type buttons */}
      <div className="filter_buttons">
        {selectTypeLabel && (
          <label className="dashboard_filter_label">{t("Select Type")}</label>
        )}
        <div className="type_buttons_wrapper">
          {FILTERS.map((item) => (
            <button
              key={item}
              className={`main_btn ${active === item ? "active" : ""}`}
              onClick={() => applyPreset(item)}
            >
              {t(FILTER_LABELS[item])}
            </button>
          ))}
        </div>
      </div>

      {/*
        Always a range picker.
        `dates` is fed back as [snappedFrom, snappedTo] so the calendar
        visually highlights the full day / week / month after a pick.
      */}
      <DateRangePicker
        ref={pickerRef}
        dates={active === "DAY" ? [dates?.[0]] : dates} // ← display only
        setDates={handleManualDateChange}
        isDateRangePicker={active !== "DAY"}
        className={active !== "DAY" ? "range-selected" : "day-selected"}
      />

      <div className="d-flex gap-2 align-items-center action_buttons">
        <button
          className="main_btn reset-filter"
          type="button"
          onClick={handleReset}
        >
          <img src={refreshDashboard} width={20} height={20} alt="reset" />
        </button>

        <button
          className="main_btn nav-arrow"
          type="button"
          onClick={handlePrev}
          disabled={leftDisabled}
          title={t("Previous period")}
          style={{
            opacity: leftDisabled ? 0.4 : 1,
            cursor: leftDisabled ? "not-allowed" : "pointer",
          }}
        >
          {isRTL ? "→" : "←"}
        </button>

        <button
          className="main_btn nav-arrow"
          type="button"
          onClick={handleNext}
          disabled={rightDisabled}
          title={t("Next period")}
          style={{
            opacity: rightDisabled ? 0.4 : 1,
            cursor: rightDisabled ? "not-allowed" : "pointer",
          }}
        >
          {isRTL ? "←" : "→"}
        </button>
      </div>
    </div>
  );
};

export default CustomerLeadsFilter;
